import { FilterOutlined } from '@ant-design/icons'
import { Button, theme } from 'antd'
import { useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMediaQuery } from '../hooks'
import { routePaths } from '../pages/RootPage'
import MainHeaderAuditTrailsModal from './MainHeaderAuditTrailsModal'
import SelectAuditTrailsComponent from './SelectAuditTrailsComponent'
import SelectTimePeriod, {
  SelectTimePeriodOnChangeParams,
  TimePeriodValue
} from './SelectTimePeriod'

interface BuildUrlParams {
  component?: string | null
  timePeriod?: string | null
  startDate?: string | null
  endDate?: string | null
}

const { useToken } = theme

const MainHeaderAuditTrails = () => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const query = useMemo(() => new URLSearchParams(search), [search])
  const { token } = useToken()
  const isXs = useMediaQuery('xs')
  const isSm = useMediaQuery('sm')
  const isMobile = isXs || isSm
  const [isModalVisible, setIsModalVisible] = useState(false)

  const buildUrl = useCallback((params: BuildUrlParams) => {
    const haveParameter = params.component || params.timePeriod
    let url = haveParameter ? '?' : ''
    if (params?.component) {
      url = url.concat(`component=${params.component || ''}`)
    }
    if (params.timePeriod) {
      url = url.concat(
        url.length > 1 ? `&` : ``,
        `timePeriod=${params.timePeriod || ''}`
      )
      if (params.startDate) {
        url = url.concat(
          url.length > 1 ? `&` : ``,
          `startDate=${params.startDate || ''}`
        )
      }
      if (params.endDate) {
        url = url.concat(
          url.length > 1 ? `&` : ``,
          `endDate=${params.endDate || ''}`
        )
      }
    }
    return url
  }, [])

  const handleComponentChange = useCallback(
    (value?: string) => {
      const urlParams = buildUrl({
        component: value,
        timePeriod: query.get('timePeriod'),
        startDate: query.get('startDate'),
        endDate: query.get('endDate'),
      })
      navigate(`${routePaths.AUDIT_TRAIL_LIST}${urlParams}`)
    },
    [navigate, buildUrl, query]
  )

  const handleTimePeriodChange = useCallback(
    (value?: SelectTimePeriodOnChangeParams) => {
      const urlParams = buildUrl({
        component: query.get('component'),
        timePeriod: value?.timePeriodValue,
        startDate: value?.startDate,
        endDate: value?.endDate,
      })
      navigate(`${routePaths.AUDIT_TRAIL_LIST}${urlParams}`)
    },
    [navigate, buildUrl, query]
  )

  const onCloseModal = useCallback(() => {
    handleComponentChange(undefined)
    handleTimePeriodChange(undefined)
    setIsModalVisible(false)
  }, [handleComponentChange, handleTimePeriodChange])

  return (
    <>
      {isMobile ? (
        <>
          <Button
            icon={<FilterOutlined />}
            onClick={() => setIsModalVisible(true)}
            style={{
              backgroundColor: token.colorBgContainer,
              color: token.colorText,
              border: `1px solid ${token.colorBorder}`,
            }}
          />
          <MainHeaderAuditTrailsModal
            isVisible={isModalVisible}
            onClose={onCloseModal}
            onComponentChange={handleComponentChange}
            onTimePeriodChange={handleTimePeriodChange}
            componentValue={query.get('component') || ''}
            timePeriodValue={query.get('timePeriod') as TimePeriodValue}
          />
        </>
      ) : (
        <>
          <SelectAuditTrailsComponent
            onChange={handleComponentChange}
            value={query.get('component') || ''}
          />
          <SelectTimePeriod
            onChange={handleTimePeriodChange}
            defaultValue={query.get('timePeriod') as TimePeriodValue}
          />
        </>
      )}
    </>
  )
}

export default MainHeaderAuditTrails
